// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file PanelSearchInput.tsx
 * @input Uses React, StyleX, Icon, InputClearButton, theme tokens
 * @output Exports PanelSearchInput — the search row that sits at the top of a
 *   dropdown panel
 * @position Shared internal primitive; used by Selector and MultiSelector
 *
 * A dropdown panel is already a bordered, elevated surface. Nesting a bordered
 * TextInput inside it draws a second box within that box, so the field reads as
 * a control dropped into the menu rather than part of it. This row is the
 * seamless alternative — magnifier, borderless input, clear button — separated
 * from the options by a divider the panel owns.
 *
 * The field is shaped like the option rows under it rather than like a form
 * input: same inset from the panel edge, same `--radius-element` corners, same
 * height. The focus ring is drawn on that rounded box, so focus reads as "this
 * row of the panel" — the same visual language as an item's highlight — instead
 * of ringing the whole panel header. It appears for keyboard focus only; see
 * `fieldKeyboardFocus` for why `:focus-visible` alone does not mean that.
 *
 *   ┌─ panel ──────────────────┐
 *   │ ┌──────────────────────┐ │  ← field: rounded, inset, item-sized
 *   │ │ ⌕  Search…         ✕ │ │
 *   │ └──────────────────────┘ │
 *   ├──────────────────────────┤  ← divider (full-bleed)
 *   │ ┌──────────────────────┐ │
 *   │ │ Apple                │ │  ← option row: same box
 *
 * The clear button is the shared `InputClearButton`, so the affordance and its
 * ghost-button behavior match every other input's clear. It renders AFTER the
 * input in DOM order: the selector components' Tab handling depends on that
 * order to keep the popup open while focus moves onto it.
 *
 * Not exported from the package: it is an implementation detail of the panels
 * that use it, not public API.
 */
import { useCallback, useEffect, useState } from 'react';
import * as stylex from '@stylexjs/stylex';
import { Icon } from "../Icon/index.js";
import { InputClearButton } from "./InputClearButton.js";
import { mergeProps } from "../utils/index.js";
import { getInteractionModality, trackInteractionModality } from "../utils/interactionModality.js";
import "../theme/tokens.stylex.js";
import { colorVars, durationVars, easeVars, radiusVars, spacingVars, typeScaleVars, typographyVars } from "../theme/tokens.stylex.js";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const styles = {
  wrapper: {
    k8WAf4: "xu0wf1k",
    kg3NbH: "x7a5moj",
    $$css: true
  },
  icon: {
    k1xSpc: "x78zum5",
    kGNEyG: "x6s0dn4",
    kmuXW: "x2lah0s",
    $$css: true
  }
};
/**
 * Search row for the top of a dropdown panel: magnifier, borderless input, and
 * a clear button once a query is typed, in a rounded box shaped like the
 * option rows beneath it.
 *
 * `className`/`style`/`xstyle` apply to the outer row (use them to match the
 * option list's inline padding); every other prop (`role`, `aria-*`, `id`, …)
 * passes through to the `<input>`, so the caller owns the combobox wiring.
 */
export function PanelSearchInput({
  ref,
  label,
  clearLabel,
  placeholder,
  value,
  onValueChange,
  onKeyDown,
  onFocus,
  onBlur,
  onContainerKeyDown,
  xstyle,
  className,
  style,
  ...props
}) {
  // `:focus-visible` matches a text input focused by POINTER as well (CSS
  // Selectors 4, verified in Chromium), so it cannot express "keyboard focus"
  // by itself. Gate it on how the user last interacted; the CSS condition
  // stays `:focus-visible`, this only narrows it.
  const [isKeyboardFocus, setIsKeyboardFocus] = useState(false);
  useEffect(() => {
    trackInteractionModality();
  }, []);
  const handleFocus = useCallback(e => {
    setIsKeyboardFocus(getInteractionModality() === 'keyboard');
    onFocus?.(e);
  }, [onFocus]);
  const handleBlur = useCallback(e => {
    setIsKeyboardFocus(false);
    onBlur?.(e);
  }, [onBlur]);
  const handleClear = useCallback(() => {
    onValueChange('');
    // Clearing puts the caret back where the user was typing, matching
    // TextInput's built-in clear.
    if (typeof ref === 'object' && ref?.current) {
      ref.current.focus();
    }
  }, [onValueChange, ref]);
  return /*#__PURE__*/_jsx("div", {
    onKeyDown: onContainerKeyDown,
    ...mergeProps(stylex.props(styles.wrapper, xstyle), className, style),
    children: /*#__PURE__*/_jsxs("div", {
      "data-keyboard-focus": isKeyboardFocus ? 'true' : undefined,
      ...{
        0: {
          className: "x9f619 x78zum5 x6s0dn4 x1txdalj xh8yej3 x1vofgu7 xf314gf xh6dtrn xkdsq27 xuedmi6 x12w9bfk xlr8y92"
        },
        1: {
          className: "x9f619 x78zum5 x6s0dn4 x1txdalj xh8yej3 x1vofgu7 xf314gf xh6dtrn xkdsq27 xuedmi6 x12w9bfk xlr8y92 x1gnnqk1 x1btxeh5"
        }
      }[!!isKeyboardFocus << 0],
      children: [/*#__PURE__*/_jsx(Icon, {
        icon: "search",
        size: "sm",
        color: "secondary",
        xstyle: styles.icon
      }), /*#__PURE__*/_jsx("input", {
        ref: ref,
        type: "text",
        "aria-label": label,
        placeholder: placeholder,
        value: value,
        onChange: e => onValueChange(e.target.value),
        onKeyDown: onKeyDown,
        onFocus: handleFocus,
        onBlur: handleBlur,
        ...{
          className: "x1iyjqo2 xs83m0k xeuugli x1717udv x1ghz6dp xc342km xng3xce xjbqb8w x1tgivj0 x9ynric xcr08ib xdk3u5w x1kq96og x1a2a7pz xeyghm5"
        },
        ...props
      }), value !== '' && /*#__PURE__*/_jsx(InputClearButton, {
        label: clearLabel,
        onClick: handleClear
      })]
    })
  });
}
PanelSearchInput.displayName = 'PanelSearchInput';